import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Calendar,
  Users,
  DollarSign,
  Eye,
  Search,
  Filter,
  Upload,
  X,
  Video,
  Image,
  Archive,
  ArchiveRestore,
  Type,
  Palette,
  FileText
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import AdminLayout from "./AdminLayout";
import RichTextEditor from "../../components/RichTextEditor";
import ImageUpload from "../../components/ImageUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminEventsPage = () => {
  const [events, setEvents] = useState([]);
  const [archivedEvents, setArchivedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [ticketStatusColors, setTicketStatusColors] = useState({
    available: '#22C55E',
    limited: '#F59E0B',
    sold_out: '#EF4444',
    coming_soon: '#3B82F6'
  });

  useEffect(() => {
    fetchEvents();
    fetchArchivedEvents();
    fetchSiteSettings();
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/site-settings`);
      if (response.data?.ticket_status_colors) {
        setTicketStatusColors(response.data.ticket_status_colors);
      }
    } catch (error) {
      console.error("Error fetching site settings:", error);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/admin/events`);
      setEvents(response.data);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedEvents = async () => {
    try {
      const response = await axios.get(`${API}/admin/events/archived`);
      setArchivedEvents(response.data);
    } catch (error) {
      console.error("Error fetching archived events:", error);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;
    
    try {
      await axios.delete(`${API}/admin/events/${eventId}`);
      toast.success("Event deleted");
      fetchEvents();
      fetchArchivedEvents();
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  const handleArchiveEvent = async (eventId) => {
    try {
      await axios.put(`${API}/admin/events/${eventId}/archive`);
      toast.success("Event archived");
      fetchEvents();
      fetchArchivedEvents();
    } catch (error) {
      toast.error("Failed to archive event");
    }
  };

  const handleUnarchiveEvent = async (eventId) => {
    try {
      await axios.put(`${API}/admin/events/${eventId}/unarchive`);
      toast.success("Event restored");
      fetchEvents();
      fetchArchivedEvents();
    } catch (error) {
      toast.error("Failed to restore event");
    }
  };

  const filteredEvents = (showArchived ? archivedEvents : events).filter(event =>
    event.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    // Parse date as local time to avoid timezone shift
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-events">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-2">
              Events Management
            </h1>
            <p className="text-[#A1A1AA]">Create and manage your events with full landing page support</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="create-event-btn"
          >
            <Plus className="w-5 h-5" />
            Create Event
          </button>
        </div>

        {/* Tabs & Search */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          {/* Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setShowArchived(false)}
              className={`px-4 py-2 rounded transition-colors ${
                !showArchived 
                  ? "bg-[#D4AF37] text-[#050505]" 
                  : "bg-[#1A1A1A] text-[#A1A1AA] hover:text-[#D4AF37]"
              }`}
            >
              Active ({events.length})
            </button>
            <button
              onClick={() => setShowArchived(true)}
              className={`px-4 py-2 rounded transition-colors flex items-center gap-2 ${
                showArchived 
                  ? "bg-[#D4AF37] text-[#050505]" 
                  : "bg-[#1A1A1A] text-[#A1A1AA] hover:text-[#D4AF37]"
              }`}
            >
              <Archive className="w-4 h-4" />
              Archived ({archivedEvents.length})
            </button>
          </div>

          {/* Search */}
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#a1a1aa]" />
            <input
              type="text"
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 pl-10 focus:border-[#D4AF37] focus:outline-none"
              data-testid="search-events"
            />
          </div>
        </div>

        {/* Events List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="text-center py-20 bg-[#121212] border border-[rgba(212,175,55,0.15)]">
            <Calendar className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
            <h3 className="text-xl font-['Playfair_Display'] text-[#F5F5F0] mb-2">No Events Found</h3>
            <p className="text-[#A1A1AA] mb-6">Create your first event to get started</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
            >
              Create Event
            </button>
          </div>
        ) : (
          <div className="grid gap-6">
            {filteredEvents.map((event) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
              >
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Event Image */}
                  {event.banner_image && (
                    <div className="w-full lg:w-48 h-32 flex-shrink-0">
                      <img
                        src={event.banner_image}
                        alt={event.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {/* Event Details */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="px-2 py-0.5 bg-[rgba(212,175,55,0.2)] text-[#D4AF37] text-xs uppercase tracking-wider">
                            {event.category}
                          </span>
                          {event.is_active === false && (
                            <span className="px-2 py-0.5 bg-red-900/30 text-red-500 text-xs uppercase">
                              Inactive
                            </span>
                          )}
                        </div>
                        <h3 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
                          {event.title}
                        </h3>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <a
                          href={`/events/${event.id}/register`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 border border-[rgba(212,175,55,0.3)] text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors"
                          title="View Event"
                        >
                          <Eye className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => setEditingEvent(event)}
                          className="p-2 border border-[rgba(212,175,55,0.3)] text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors"
                          title="Edit Event"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {!showArchived ? (
                          <button
                            onClick={() => handleArchiveEvent(event.id)}
                            className="p-2 border border-[rgba(212,175,55,0.3)] text-[#A1A1AA] hover:text-orange-500 hover:border-orange-500 transition-colors"
                            title="Archive Event"
                          >
                            <Archive className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleUnarchiveEvent(event.id)}
                            className="p-2 border border-[rgba(212,175,55,0.3)] text-[#A1A1AA] hover:text-green-500 hover:border-green-500 transition-colors"
                            title="Restore Event"
                          >
                            <ArchiveRestore className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteEvent(event.id)}
                          className="p-2 border border-[rgba(212,175,55,0.3)] text-[#A1A1AA] hover:text-red-500 hover:border-red-500 transition-colors"
                          title="Delete Event"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-6 text-sm text-[#A1A1AA] mb-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[#D4AF37]" />
                        <span>{formatDate(event.date)} • {event.time}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-[#D4AF37]" />
                        <span>{event.location}</span>
                      </div>
                    </div>

                    {/* Ticket Options Summary */}
                    {event.ticket_options && (
                      <div className="flex flex-wrap gap-3">
                        {event.ticket_options.map((ticket, i) => (
                          <div
                            key={i}
                            className="px-3 py-2 bg-[#0F0F0F] border border-[rgba(212,175,55,0.1)]"
                          >
                            <span className="text-[#F5F5F0] text-sm">{ticket.name}</span>
                            <span className="text-[#D4AF37] text-sm ml-2">C${ticket.price}</span>
                            {ticket.capacity && (
                              <span className="text-[#a1a1aa] text-xs ml-2">({ticket.capacity} cap)</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Create/Edit Event Modal */}
        {(showCreateModal || editingEvent) && (
          <EventModal
            event={editingEvent}
            onClose={() => {
              setShowCreateModal(false);
              setEditingEvent(null);
            }}
            onSave={() => {
              setShowCreateModal(false);
              setEditingEvent(null);
              fetchEvents();
            }}
            ticketStatusColors={ticketStatusColors}
          />
        )}
      </div>
    </AdminLayout>
  );
};

// Event Modal Component
const EventModal = ({ event, onClose, onSave, ticketStatusColors = {
  available: '#22C55E',
  limited: '#F59E0B',
  sold_out: '#EF4444',
  coming_soon: '#3B82F6'
}}) => {
  const [formData, setFormData] = useState({
    title: event?.title || "",
    subtitle: event?.subtitle || "",
    tagline: event?.tagline || "",
    date: event?.date || "",
    time: event?.time || "",
    location: event?.location || "",
    address: event?.address || "",
    banner_image: event?.banner_image || "",
    video_url: event?.video_url || "",
    description: event?.description || event?.overview || "",
    overview: event?.overview || "",
    what_youll_do: event?.what_youll_do || [],
    highlights: event?.highlights || [],
    included: event?.included || [],
    category: event?.category || "Workshop",
    is_active: event?.is_active !== false,
    ticket_options: event?.ticket_options || [
      { name: "General", price: 50, capacity: 100, status: "available", sold: 0, description: "", perks: [] }
    ],
    // Landing page styling
    title_color: event?.title_color || "#F5F5F0",
    subtitle_color: event?.subtitle_color || "#D4AF37",
    button_text: event?.button_text || "Book Tickets",
    button_color: event?.button_color || "#D4AF37",
    cta_text: event?.cta_text || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const bannerInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum 10MB");
      return;
    }
    
    setUploadingBanner(true);
    const uploadData = new FormData();
    uploadData.append("file", file);
    uploadData.append("folder", "events");
    
    try {
      const response = await axios.post(`${API}/admin/upload/image`, uploadData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setFormData({ ...formData, banner_image: response.data.url });
      toast.success("Banner uploaded");
    } catch (error) {
      toast.error("Failed to upload banner");
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 100 * 1024 * 1024) {
      toast.error("File too large. Maximum 100MB");
      return;
    }
    
    setUploadingVideo(true);
    const uploadData = new FormData();
    uploadData.append("file", file);
    
    try {
      const response = await axios.post(`${API}/admin/upload/video`, uploadData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setFormData({ ...formData, video_url: response.data.url });
      toast.success("Video uploaded");
    } catch (error) {
      toast.error("Failed to upload video");
    } finally {
      setUploadingVideo(false);
    }
  };

  const getMediaUrl = (url) => {
    if (url && url.startsWith("/uploads")) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    return url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (event) {
        await axios.put(`${API}/admin/events/${event.id}/full`, formData);
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/admin/events/create`, formData);
        toast.success("Event created");
      }
      onSave();
    } catch (error) {
      toast.error("Failed to save event");
    } finally {
      setSaving(false);
    }
  };

  const addTicketOption = () => {
    setFormData({
      ...formData,
      ticket_options: [
        ...formData.ticket_options,
        { name: "", price: 0, capacity: 0, status: "available", sold: 0 }
      ]
    });
  };

  const updateTicketOption = (index, field, value) => {
    const updated = [...formData.ticket_options];
    if (field === 'price' || field === 'capacity' || field === 'sold') {
      updated[index][field] = Number(value);
    } else {
      updated[index][field] = value;
    }
    setFormData({ ...formData, ticket_options: updated });
  };

  const removeTicketOption = (index) => {
    const updated = formData.ticket_options.filter((_, i) => i !== index);
    setFormData({ ...formData, ticket_options: updated });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="p-6 border-b border-[rgba(212,175,55,0.15)] flex items-center justify-between">
          <h2 className="text-2xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
            {event ? "Edit Event Landing Page" : "Create Event Landing Page"}
          </h2>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-[#F5F5F0]">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[rgba(212,175,55,0.15)]">
          {[
            { id: "basic", label: "Basic Info", icon: FileText },
            { id: "content", label: "Content & Description", icon: Type },
            { id: "media", label: "Media", icon: Image },
            { id: "tickets", label: "Tickets", icon: DollarSign },
            { id: "styling", label: "Styling", icon: Palette },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                activeTab === tab.id
                  ? "text-[#D4AF37] border-b-2 border-[#D4AF37] bg-[rgba(212,175,55,0.05)]"
                  : "text-[#A1A1AA] hover:text-[#F5F5F0]"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {/* Basic Info Tab */}
          {activeTab === "basic" && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Event Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  >
                    <option value="Workshop">Workshop</option>
                    <option value="Girls Night">Girls Night</option>
                    <option value="Networking">Networking</option>
                    <option value="Social">Social</option>
                    <option value="Corporate">Corporate</option>
                    <option value="Wine Tasting">Wine Tasting</option>
                    <option value="Special Event">Special Event</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="form-label">Subtitle</label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  placeholder="A brief subtitle for the hero section"
                  className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                />
              </div>

              <div>
                <label className="form-label">Tagline (Italic Quote)</label>
                <input
                  type="text"
                  value={formData.tagline}
                  onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                  placeholder="A memorable quote or tagline"
                  className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none italic"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Date *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Time *</label>
                  <input
                    type="text"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    placeholder="6:00 PM - 9:00 PM"
                    className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Location *</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Full Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="text-[#A1A1AA]">Event Active:</label>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    formData.is_active ? 'bg-green-600' : 'bg-[#52525B]'
                  }`}
                >
                  <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${
                    formData.is_active ? 'left-8' : 'left-1'
                  }`} />
                </button>
                <span className="text-[#a1a1aa] text-sm">
                  {formData.is_active ? "Visible on website" : "Hidden from website"}
                </span>
              </div>
            </div>
          )}

          {/* Content Tab */}
          {activeTab === "content" && (
            <div className="space-y-6">
              <div>
                <label className="form-label">Event Description (Rich Text)</label>
                <p className="text-[#a1a1aa] text-xs mb-2">Use the toolbar to format text with bold, italic, underline, colors, and more.</p>
                <RichTextEditor
                  content={formData.description}
                  onChange={(html) => setFormData({ ...formData, description: html })}
                  placeholder="Describe your event in detail..."
                />
              </div>

              <div>
                <label className="form-label">Event Highlights</label>
                <p className="text-[#a1a1aa] text-xs mb-2">Add key highlights (one per line)</p>
                <textarea
                  value={formData.highlights?.join('\n') || ''}
                  onChange={(e) => setFormData({ ...formData, highlights: e.target.value.split('\n').filter(h => h.trim()) })}
                  rows={4}
                  placeholder="Live DJ performance&#10;Premium mocktails included&#10;Networking opportunities"
                  className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none resize-none"
                />
              </div>

              <div>
                <label className="form-label">What's Included</label>
                <p className="text-[#a1a1aa] text-xs mb-2">Add items included with ticket (one per line)</p>
                <textarea
                  value={formData.included?.join('\n') || ''}
                  onChange={(e) => setFormData({ ...formData, included: e.target.value.split('\n').filter(h => h.trim()) })}
                  rows={4}
                  placeholder="Welcome drink&#10;Appetizers&#10;Access to all activities"
                  className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none resize-none"
                />
              </div>
            </div>
          )}

          {/* Media Tab */}
          {activeTab === "media" && (
            <div className="space-y-6">
              {/* Banner Image */}
              <div>
                <p className="text-[#a1a1aa] text-xs mb-2">This image appears in the hero section of your event landing page.</p>
                <ImageUpload
                  label="Banner Image"
                  value={formData.banner_image}
                  onChange={(url) => setFormData({ ...formData, banner_image: url })}
                  folder="events"
                  placeholder="Upload banner or enter URL"
                  previewHeight="h-48"
                />
              </div>

              {/* Video */}
              <div>
                <label className="form-label flex items-center gap-2">
                  <Video className="w-4 h-4" /> Promo Video
                </label>
                <p className="text-[#a1a1aa] text-xs mb-2">Add a promotional video for your event landing page.</p>
                <div className="flex gap-4 items-start">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={formData.video_url}
                      onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                      placeholder="Video URL (YouTube/uploaded)"
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    />
                  </div>
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => videoInputRef.current?.click()}
                    disabled={uploadingVideo}
                    className="px-4 py-3 border border-[rgba(212,175,55,0.3)] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#050505] transition-colors flex items-center gap-2"
                  >
                    <Video className="w-4 h-4" />
                    {uploadingVideo ? "Uploading..." : "Upload"}
                  </button>
                </div>
                {formData.video_url && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-[#A1A1AA]">
                    <Video className="w-4 h-4 text-[#D4AF37]" />
                    Video set: {formData.video_url.split('/').pop()}
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, video_url: "" })}
                      className="text-red-500 hover:text-red-400"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tickets Tab */}
          {activeTab === "tickets" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <label className="form-label mb-0">Ticket Options</label>
                  <p className="text-[#a1a1aa] text-xs">Create different ticket tiers with prices and perks.</p>
                </div>
                <button
                  type="button"
                  onClick={addTicketOption}
                  className="btn-secondary text-sm flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Add Ticket Type
                </button>
              </div>
              
              <div className="space-y-4">
                {formData.ticket_options.map((ticket, index) => (
                  <div key={index} className="p-4 bg-[#121212] border border-[rgba(212,175,55,0.1)]">
                    <div className="flex gap-4 items-start flex-wrap mb-4">
                      <div className="flex-1 min-w-[150px]">
                        <label className="text-[#a1a1aa] text-xs mb-1 block">Ticket Name *</label>
                        <input
                          type="text"
                          value={ticket.name}
                          onChange={(e) => updateTicketOption(index, 'name', e.target.value)}
                          className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                          placeholder="e.g., Early Bird"
                        />
                      </div>
                      <div className="w-24">
                        <label className="text-[#a1a1aa] text-xs mb-1 block">Price (C$)</label>
                        <input
                          type="number"
                          value={ticket.price}
                          onChange={(e) => updateTicketOption(index, 'price', e.target.value)}
                          className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                          min="0"
                        />
                      </div>
                      <div className="w-24">
                        <label className="text-[#a1a1aa] text-xs mb-1 block">Capacity</label>
                        <input
                          type="number"
                          value={ticket.capacity || ""}
                          onChange={(e) => updateTicketOption(index, 'capacity', e.target.value)}
                          className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                          min="0"
                          placeholder="0 = unlimited"
                        />
                      </div>
                      <div className="w-32">
                        <label className="text-[#a1a1aa] text-xs mb-1 block">Status</label>
                        <select
                          value={ticket.status}
                          onChange={(e) => updateTicketOption(index, 'status', e.target.value)}
                          className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none text-sm"
                          style={{ 
                            borderLeftWidth: '4px',
                            borderLeftColor: ticketStatusColors[ticket.status] || '#22C55E'
                          }}
                        >
                          <option value="available" style={{ color: ticketStatusColors.available }}>Available</option>
                          <option value="limited" style={{ color: ticketStatusColors.limited }}>Limited</option>
                          <option value="sold_out" style={{ color: ticketStatusColors.sold_out }}>Sold Out</option>
                          <option value="coming_soon" style={{ color: ticketStatusColors.coming_soon }}>Coming Soon</option>
                        </select>
                        <div 
                          className="mt-1 h-1 rounded"
                          style={{ backgroundColor: ticketStatusColors[ticket.status] || '#22C55E' }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeTicketOption(index)}
                        className="p-2 text-red-500 hover:bg-red-500/10 mt-6"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="mb-3">
                      <label className="text-[#a1a1aa] text-xs mb-1 block">Description</label>
                      <input
                        type="text"
                        value={ticket.description || ''}
                        onChange={(e) => updateTicketOption(index, 'description', e.target.value)}
                        className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                        placeholder="Brief description of this ticket tier"
                      />
                    </div>
                    
                    <div>
                      <label className="text-[#a1a1aa] text-xs mb-1 block">Perks (one per line)</label>
                      <textarea
                        value={ticket.perks?.join('\n') || ''}
                        onChange={(e) => updateTicketOption(index, 'perks', e.target.value.split('\n').filter(p => p.trim()))}
                        rows={2}
                        className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none resize-none text-sm"
                        placeholder="Priority seating&#10;Exclusive welcome drink"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Styling Tab */}
          {activeTab === "styling" && (
            <div className="space-y-6">
              <div className="p-4 bg-[#121212] border border-[rgba(212,175,55,0.1)]">
                <h3 className="text-[#D4AF37] font-medium mb-4">Hero Section Styling</h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">Title Color</label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        value={formData.title_color}
                        onChange={(e) => setFormData({ ...formData, title_color: e.target.value })}
                        className="w-12 h-10 bg-transparent border border-[rgba(212,175,55,0.3)] cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.title_color}
                        onChange={(e) => setFormData({ ...formData, title_color: e.target.value })}
                        className="flex-1 bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="form-label">Subtitle Color</label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        value={formData.subtitle_color}
                        onChange={(e) => setFormData({ ...formData, subtitle_color: e.target.value })}
                        className="w-12 h-10 bg-transparent border border-[rgba(212,175,55,0.3)] cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.subtitle_color}
                        onChange={(e) => setFormData({ ...formData, subtitle_color: e.target.value })}
                        className="flex-1 bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-[#121212] border border-[rgba(212,175,55,0.1)]">
                <h3 className="text-[#D4AF37] font-medium mb-4">Call to Action Button</h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">Button Text</label>
                    <input
                      type="text"
                      value={formData.button_text}
                      onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
                      className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                      placeholder="Book Tickets"
                    />
                  </div>
                  <div>
                    <label className="form-label">Button Color</label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        value={formData.button_color}
                        onChange={(e) => setFormData({ ...formData, button_color: e.target.value })}
                        className="w-12 h-10 bg-transparent border border-[rgba(212,175,55,0.3)] cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.button_color}
                        onChange={(e) => setFormData({ ...formData, button_color: e.target.value })}
                        className="flex-1 bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="form-label">Additional CTA Text</label>
                  <input
                    type="text"
                    value={formData.cta_text}
                    onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                    placeholder="Limited spots available!"
                  />
                </div>
              </div>

              {/* Preview */}
              <div className="p-4 bg-[#0a0a0a] border border-[rgba(212,175,55,0.1)]">
                <h3 className="text-[#a1a1aa] text-sm mb-4">Preview</h3>
                <div className="p-6 bg-[#050505] border border-[rgba(212,175,55,0.2)]">
                  <span className="inline-block bg-[#D4AF37] text-[#050505] px-3 py-1 text-xs uppercase tracking-widest font-semibold mb-3">
                    {formData.category}
                  </span>
                  <h2 className="text-2xl font-['Playfair_Display'] font-bold mb-2" style={{ color: formData.title_color }}>
                    {formData.title || "Event Title"}
                  </h2>
                  {formData.subtitle && (
                    <p className="mb-2" style={{ color: formData.subtitle_color }}>
                      {formData.subtitle}
                    </p>
                  )}
                  {formData.tagline && (
                    <p className="text-[#F5F5F0] italic mb-4">"{formData.tagline}"</p>
                  )}
                  <button
                    type="button"
                    className="px-6 py-2 font-semibold text-[#050505]"
                    style={{ backgroundColor: formData.button_color }}
                  >
                    {formData.button_text}
                  </button>
                  {formData.cta_text && (
                    <p className="text-[#A1A1AA] text-sm mt-2">{formData.cta_text}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="flex gap-4 pt-6 border-t border-[rgba(212,175,55,0.15)] mt-6">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-5 h-5 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  {event ? "Update Event" : "Create Event"}
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AdminEventsPage;
